const regionTemplate = () => ({
  bmcG: null,
  fatMassG: null,
  leanMassG: null,
  leanPlusBmcG: null,
  totalMassG: null,
  fatPercent: null,
  percentileYoung: null,
  percentileAgeMatched: null,
});

const bmdTemplate = () => ({
  areaCm2: null,
  bmcG: null,
  bmd: null,
  tScore: null,
  referenceMeanPercent: null,
  zScore: null,
  ageMatchedPercent: null,
});

const hipSideTemplate = () => ({
  neck: bmdTemplate(),
  total: bmdTemplate(),
});

export function createEmptyRecord() {
  return {
    schemaVersion: 2,
    patient: {
      name: "",
      patientId: "",
      birthDate: "",
      age: null,
      sex: "",
      ethnicity: "",
      heightCm: null,
      scaleWeightKg: null,
    },
    exam: {
      date: "",
      examId: "",
      vendor: "",
      model: "",
      analysisVersion: "",
      referrer: "",
      sport: "",
      sourceFileName: "",
    },
    composition: {
      total: regionTemplate(),
      subtotal: regionTemplate(),
      regions: {
        leftArm: regionTemplate(),
        rightArm: regionTemplate(),
        trunk: regionTemplate(),
        leftLeg: regionTemplate(),
        rightLeg: regionTemplate(),
        head: regionTemplate(),
        android: regionTemplate(),
        gynoid: regionTemplate(),
      },
      indices: {
        bmi: null,
        fmi: null,
        androidGynoidRatio: null,
        trunkLegFatRatio: null,
        trunkLegFatPercentileYoung: null,
        trunkLegFatPercentileAgeMatched: null,
        trunkExtremityFatRatio: null,
        trunkExtremityFatPercentileYoung: null,
        trunkExtremityFatPercentileAgeMatched: null,
        vatMassG: null,
        vatVolumeCm3: null,
        vatAreaCm2: null,
        lmi: null,
        almi: null,
        bodyFatPercentileYoung: null,
        bodyFatPercentileAgeMatched: null,
        fmiPercentileYoung: null,
        fmiPercentileAgeMatched: null,
        lmiPercentileYoung: null,
        lmiPercentileAgeMatched: null,
        almiPercentileYoung: null,
        almiPercentileAgeMatched: null,
      },
    },
    bone: {
      wholeBody: bmdTemplate(),
      wholeBodyRegions: {
        leftArm: bmdTemplate(),
        rightArm: bmdTemplate(),
        leftRibs: bmdTemplate(),
        rightRibs: bmdTemplate(),
        thoracicSpine: bmdTemplate(),
        lumbarSpine: bmdTemplate(),
        pelvis: bmdTemplate(),
        leftLeg: bmdTemplate(),
        rightLeg: bmdTemplate(),
        head: bmdTemplate(),
      },
      hip: {
        side: "",
        neck: bmdTemplate(),
        total: bmdTemplate(),
        sides: {
          left: hipSideTemplate(),
          right: hipSideTemplate(),
        },
        fraxReported: false,
        fraxReason: "",
      },
      lumbar: {
        l1: bmdTemplate(),
        l2: bmdTemplate(),
        l3: bmdTemplate(),
        l4: bmdTemplate(),
        total: bmdTemplate(),
        scmoMgCm2: null,
      },
    },
    professional: {
      populationProfile: "",
      menopausalStatus: "not_applicable",
      useWorkbookRegionalBmdRules: true,
      vatReferenceAreaCm2: 100,
      regionalBmdReferenceMin: 0.97,
      regionalBmdReferenceMax: 1.28,
      fmiReferenceMin: 5,
      fmiReferenceMax: 9,
      clinicalContext: "",
      conclusionOverride: "",
      recommendations: "",
      approvedBy: "Nutricionista Paola Hoyos",
      organization: "Centro de Ciencias del Deporte · Ministerio del Deporte",
      license: "",
    },
    source: {
      pageCount: 0,
      extractedAt: "",
      parser: "",
      confidence: 0,
      detectedFields: 0,
      warnings: [],
      pages: [],
    },
  };
}

export function createAnonymizedDemoRecord() {
  const record = createEmptyRecord();
  record.professional.populationProfile = "athlete";
  record.patient = {
    ...record.patient,
    name: "Paciente de ejemplo",
    patientId: "DEMO-001",
    birthDate: "1992-03-25",
    age: 34,
    sex: "female",
    ethnicity: "Hispana",
    heightCm: 162,
    scaleWeightKg: 58.4,
  };
  record.exam = {
    ...record.exam,
    date: "2026-07-28",
    examId: "DEMO-DXA",
    vendor: "Hologic",
    model: "Horizon",
    sport: "Deporte de resistencia",
    sourceFileName: "ejemplo-anonimizado.pdf",
  };

  const regions = {
    leftArm: [123.91, 516.5, 1817.9, 1941.8, 2458.4, 21],
    rightArm: [124.35, 496.8, 1806.2, 1930.6, 2427.4, 20.5],
    trunk: [724.47, 4648.9, 22072.6, 22797.1, 27446, 16.9],
    leftLeg: [390.52, 2390, 7647.5, 8038, 10428, 22.9],
    rightLeg: [353.7, 2351.2, 7523.9, 7877.6, 10228.9, 23],
    subtotal: [1716.95, 10403.5, 40868.2, 42585.2, 52988.7, 19.6],
    head: [542.19, 831, 2736.5, 3278.7, 4109.6, 20.2],
    total: [2259.14, 11234.5, 43604.7, 45863.8, 57098.3, 19.7],
  };
  const toRegion = ([bmcG, fatMassG, leanMassG, leanPlusBmcG, totalMassG, fatPercent]) => ({
    ...regionTemplate(),
    bmcG,
    fatMassG,
    leanMassG,
    leanPlusBmcG,
    totalMassG,
    fatPercent,
  });
  record.composition.total = {
    ...toRegion(regions.total),
    percentileYoung: 1,
    percentileAgeMatched: 1,
  };
  record.composition.subtotal = toRegion(regions.subtotal);
  for (const key of ["leftArm", "rightArm", "trunk", "leftLeg", "rightLeg", "head"]) {
    record.composition.regions[key] = toRegion(regions[key]);
  }
  record.composition.regions.android = {
    ...regionTemplate(),
    fatMassG: 609,
    leanPlusBmcG: 3330,
    totalMassG: 3940,
    fatPercent: 15.5,
  };
  record.composition.regions.gynoid = {
    ...regionTemplate(),
    fatMassG: 2409,
    leanPlusBmcG: 8082,
    totalMassG: 10491,
    fatPercent: 23,
  };
  record.composition.indices = {
    ...record.composition.indices,
    bmi: 22.3,
    fmi: 4.28,
    androidGynoidRatio: 0.67,
    trunkLegFatRatio: 0.74,
    trunkLegFatPercentileYoung: 22,
    trunkLegFatPercentileAgeMatched: 17,
    trunkExtremityFatRatio: 0.81,
    trunkExtremityFatPercentileYoung: 28,
    trunkExtremityFatPercentileAgeMatched: 20,
    vatMassG: 129,
    vatVolumeCm3: 140,
    vatAreaCm2: 26.8,
    lmi: 16.6,
    almi: 7.16,
    bodyFatPercentileYoung: 1,
    bodyFatPercentileAgeMatched: 1,
    fmiPercentileYoung: 2,
    fmiPercentileAgeMatched: 1,
    lmiPercentileYoung: 54,
    lmiPercentileAgeMatched: 46,
    almiPercentileYoung: 62,
    almiPercentileAgeMatched: 56,
  };

  const setBmd = (bmd, tScore = null, zScore = null, areaCm2 = null, bmcG = null) => ({
    ...bmdTemplate(),
    areaCm2,
    bmcG,
    bmd,
    tScore,
    zScore,
  });
  record.bone.wholeBody = setBmd(1.214, 1.4, 1.2, 1860.22, 2259.14);
  record.bone.wholeBodyRegions = {
    leftArm: setBmd(0.757, null, null, 163.63, 123.91),
    rightArm: setBmd(0.753, null, null, 165.2, 124.35),
    leftRibs: setBmd(0.729, null, null, 130.51, 95.12),
    rightRibs: setBmd(0.776, null, null, 126.56, 98.26),
    thoracicSpine: setBmd(0.969, null, null, 115.92, 112.29),
    lumbarSpine: setBmd(1.037, null, null, 50.07, 51.95),
    pelvis: setBmd(1.461, null, null, 251.16, 366.85),
    leftLeg: setBmd(1.232, null, null, 317, 390.52),
    rightLeg: setBmd(1.152, null, null, 307.15, 353.7),
    head: setBmd(2.327, null, null, 233.02, 542.19),
  };
  record.bone.hip.side = "left";
  record.bone.hip.neck = {
    ...setBmd(0.855, -0.2, -0.2, 4.7, 4.02),
    referenceMeanPercent: 98,
    ageMatchedPercent: 98,
  };
  record.bone.hip.total = {
    ...setBmd(0.998, 0.3, 0.3, 32.9, 32.85),
    referenceMeanPercent: 104,
    ageMatchedPercent: 104,
  };
  record.bone.hip.sides.left = {
    neck: { ...record.bone.hip.neck },
    total: { ...record.bone.hip.total },
  };
  record.bone.hip.fraxReason = "No informado por el equipo: puntuaciones centrales iguales o mayores que -1,0.";
  record.bone.lumbar.l1 = setBmd(1.09, 0.9, 0.9, 14.28, 15.57);
  record.bone.lumbar.l2 = setBmd(1.075, 0.4, 0.5, 16.12, 17.33);
  record.bone.lumbar.l3 = setBmd(1.119, 0.3, 0.4, 16.16, 18.08);
  record.bone.lumbar.l4 = setBmd(1.244, 1.7, 1.7, 17.39, 21.63);
  record.bone.lumbar.total = setBmd(1.135, 0.8, 0.9, 63.95, 72.61);
  record.bone.lumbar.scmoMgCm2 = 1235;
  record.source = {
    ...record.source,
    pageCount: 8,
    parser: "demo-anonimizado",
    confidence: 1,
    detectedFields: 82,
    warnings: ["Ejemplo sin imágenes clínicas ni identificadores reales."],
  };
  return record;
}

export function mergeRecord(base, patch) {
  if (Array.isArray(base) || Array.isArray(patch)) return structuredClone(patch ?? base);
  if (!base || typeof base !== "object") return patch ?? base;
  const result = { ...base };
  for (const [key, value] of Object.entries(patch ?? {})) {
    result[key] = value && typeof value === "object" && !Array.isArray(value)
      ? mergeRecord(base[key] ?? {}, value)
      : value;
  }
  return result;
}
